const STATE_KEY = 'avalanche.oauth_state';
const RETURN_TO_KEY = 'avalanche.oauth_return_to';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function randomState() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// Where QuestionPro sends the user back is a property of the OAuth client
// registration ("successRedirectUrl" in QP admin), NOT of this request -
// Kryptonite's own authorize call passes only response_type and client_id
// (kryptonite auth.controller.ts:59). We still send redirect_uri so the flow
// works if QP ever honours it, but never rely on it: the code is picked up on
// whatever path QP lands on (see readAuthorizationCode).
export function buildAuthorizeUrl(returnTo) {
  const clientId = process.env.REACT_APP_QUESTIONPRO_CLIENT_ID;
  if (!clientId) {
    throw new Error(
      'REACT_APP_QUESTIONPRO_CLIENT_ID is not set - the app cannot start a QuestionPro login.',
    );
  }

  const state = randomState();
  window.sessionStorage.setItem(STATE_KEY, state);
  window.sessionStorage.setItem(RETURN_TO_KEY, returnTo || '/');

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    state,
  });
  const redirectUri = process.env.REACT_APP_QUESTIONPRO_REDIRECT_URI;
  if (redirectUri) {
    params.set('redirect_uri', redirectUri);
  }
  return `${process.env.REACT_APP_QUESTIONPRO_AUTHORIZE_URL}?${params.toString()}`;
}

// Pulls an OAuth code off the current URL wherever QuestionPro dropped us -
// the registered callback may be a bare origin ("/?code=...") rather than
// "/callback?code=...".
export function readAuthorizationCode(search = window.location.search) {
  const params = new URLSearchParams(search);
  return { code: params.get('code'), state: params.get('state') };
}

// Removes the OAuth params so a reload cannot replay a spent code.
export function stripAuthParamsFromUrl() {
  const url = new URL(window.location.href);
  ['code', 'state'].forEach((key) => url.searchParams.delete(key));
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

export function consumeState() {
  const state = window.sessionStorage.getItem(STATE_KEY);
  window.sessionStorage.removeItem(STATE_KEY);
  return state;
}

// QuestionPro validates neither `state` nor a client secret on the token
// exchange (backend/authorization/questionpro.py:exchange_code_for_token), so
// this comparison is the only CSRF defence in the flow. It can only run when QP
// echoes `state` back; if it doesn't, we say so rather than fail the login.
export function verifyState(returnedState) {
  const expected = consumeState();
  if (!returnedState) {
    // eslint-disable-next-line no-console
    console.warn('QuestionPro did not return an OAuth `state` - CSRF check skipped.');
    return true;
  }
  return Boolean(expected) && returnedState === expected;
}

export function consumeReturnTo() {
  const returnTo = window.sessionStorage.getItem(RETURN_TO_KEY);
  window.sessionStorage.removeItem(RETURN_TO_KEY);
  return returnTo || '/';
}

async function postJson(path, body) {
  const response = await fetch(`${API_BASE_URL}api/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.response?.message || 'Authentication failed');
  }
  return payload;
}

// Trades the QuestionPro authorization code for an Avalanche session JWT.
// The code is single-use on QuestionPro's side, so callers must not retry.
export async function exchangeCode(code) {
  const payload = await postJson('v1/authorization/questionpro/exchange-code/', { code });
  return payload.data.access_token;
}

export async function refreshSession(token) {
  const response = await fetch(`${API_BASE_URL}api/v1/authorization/session/refresh/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error('Session refresh failed');
  }
  const payload = await response.json();
  return payload.data.access_token;
}

export async function fetchProfile(token) {
  const response = await fetch(`${API_BASE_URL}api/v1/onboarding/users/me/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error('Could not load user profile');
  }
  const payload = await response.json();
  return payload.data;
}
